import {ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {IronContextRegistry} from 'common/report/context-registry';

import {
  DISMISS_KEY,
  PersistedDismissTooltipTour,
  TooltipTourJsxProps,
  TooltipTourParams,
  TooltipTourState,
} from './types';
import {getCurrentTooltipTourList, userDimissedTheCurrentTooltipTour} from './utils';
import {template} from './template';
import css from './index.module.styl?inline';

export class TooltipTour extends ParamComponent<TooltipTourParams, TooltipTourState, IronContextRegistry> {
  private userLocalStorageDismissTipsHelper: UserLocalStorageHelper<PersistedDismissTooltipTour>;

  get config() {
    return {
      contexts: [`localStorageManager` as const],
      params: {
        uniqueTourName: String,
        primaryButtonLabel: String,
        secondaryButtonLabel: String,
        title: String,
        markdownText: String,
        showDoNotShowCheckbox: Boolean,
        imageSrc: String,
        onPrimaryButtonClick: Function,
        onSecondaryButtonBlick: Function,
        onCheckboxClick: Function,
      },
      css,
      template,
      useShadowDom: true,
      defaultState: {
        isDismissed: false,
        isVisible: true,
      },
    };
  }

  connectedCallback(): void {
    this.userLocalStorageDismissTipsHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.DimissTooltipTour,
    });
    super.connectedCallback();
  }

  shouldRenderTour = () => {
    return (
      !userDimissedTheCurrentTooltipTour(this.params.uniqueTourName, this.userLocalStorageDismissTipsHelper) &&
      this.state.isVisible
    );
  };

  handlePrimaryButtonClick = () => {
    if (this.state.isDismissed) {
      this.dismissCurrentTooltipTour();
    }
    // Primary should always close the tooltip
    this.params.onPrimaryButtonClick?.();
    this.update({isVisible: false});
  };

  handleSecondaryButtonClik = () => {
    this.params.onSecondaryButtonBlick?.();
  };

  handleCheckboxToggle = () => {
    if (this.state.isDismissed) {
      this.update({isDismissed: false});
    } else {
      this.update({isDismissed: true});
    }
    this.params.onCheckboxClick?.();
  };

  isCheckboxMarked = () => {
    return this.state.isDismissed;
  };

  dismissCurrentTooltipTour = () => {
    const currentTooltipTour = this.params.uniqueTourName;
    const dismissedTooltipTourNames = getCurrentTooltipTourList(this.userLocalStorageDismissTipsHelper);
    if (!dismissedTooltipTourNames.includes(currentTooltipTour)) {
      dismissedTooltipTourNames.push(currentTooltipTour);
    }
    this.userLocalStorageDismissTipsHelper.setItem({
      key: DISMISS_KEY,
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      value: {tips: dismissedTooltipTourNames},
    });
  };
}

export const TooltipTourEl = defineAndMakeJsxFactory<TooltipTourJsxProps>(`mp-tooltip-tour`, TooltipTour);
export const TooltipTourReact = panel2react<TooltipTourJsxProps>(`mp-tooltip-tour`, TooltipTour);
